import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import {
  COMMON_NOTIFICATION_TYPES,
  notifications as api,
  type NotificationPreference,
  type NotificationSeverity,
} from '../../api/notifications';
import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { SkeletonRows } from '../../components/Skeleton';
import { Section } from '../../components/Surface';
import { toast } from '../../components/Toast';
import { fieldStyles } from '../../components/styles';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useAuth } from '../../stores/auth';

/**
 * La mitad de preferencias de `/settings`: la tabla de reglas que hay detrás de
 * la bandeja.
 *
 * El `PUT` sustituye la lista entera, así que la pantalla edita un borrador y
 * lo guarda de una pieza en lugar de calcular diferencias por fila: la misma
 * semántica que tiene el endpoint. La tabla vacía no es un fallo, es el valor
 * de fábrica —y la cabecera lo dice, porque leer «sin reglas» como «sin
 * notificaciones» es justo la conclusión contraria—.
 */

const SEVERITIES: NotificationSeverity[] = ['info', 'warning', 'error', 'critical'];

const NEW_RULE: NotificationPreference = {
  company_id: null,
  notification_type: '*',
  in_app: true,
  email: true,
  min_severity: 'warning',
};

/** Stable fallback — an inline `?? []` makes the store snapshot unstable and loops. */
const NO_COMPANIES: { id: string; name: string; slug: string; status: string }[] = [];

export function NotificationPreferences() {
  const t = useT();
  const client = useQueryClient();
  const companies = useAuth((state) => state.user?.companies ?? NO_COMPANIES);
  const saved = useQuery({ queryKey: ['notification-preferences'], queryFn: () => api.preferences() });

  /** Null until the user touches something; then a full working copy. */
  const [draft, setDraft] = useState<NotificationPreference[] | null>(null);
  const rows = draft ?? saved.data?.data ?? [];

  const edit = (index: number, patch: Partial<NotificationPreference>) => {
    setDraft(rows.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const save = useMutation({
    mutationFn: () => api.updatePreferences(rows),
    onSuccess: () => {
      setDraft(null);
      void client.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success(t('settings.notifications.saved'));
    },
  });

  if (saved.isPending) return <SkeletonRows rows={2} />;
  if (saved.isError) return <ErrorBanner error={saved.error} onRetry={() => void saved.refetch()} />;

  return (
    <Section
      title={t('settings.notifications.title')}
      description={
        // La severidad va en cursiva dentro de la frase, así que el marcador se
        // parte en lugar de interpolarse: la traducción decide dónde cae.
        t('settings.notifications.intro')
          .split('{warning}')
          .flatMap((part, index) => [
            part,
            index === 0 ? <em key="warning">{t('severity.warning').toLowerCase()}</em> : null,
          ])
      }
    >
      {save.error !== null && <ErrorBanner error={save.error} />}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs text-ink-faint">
                <th className="py-2 pr-3 font-semibold">{t('settings.notifications.company')}</th>
                <th className="py-2 pr-3 font-semibold">{t('settings.notifications.type')}</th>
                <th className="py-2 pr-3 font-semibold">{t('settings.notifications.inApp')}</th>
                <th className="py-2 pr-3 font-semibold">{t('settings.notifications.email')}</th>
                <th className="py-2 pr-3 font-semibold">{t('settings.notifications.minSeverity')}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((rule, index) => (
                // Index keys are fine here: the table is a draft edited in place.
                <tr key={index} className="border-b border-line">
                  <td className="py-2 pr-3">
                    <select
                      aria-label={t('settings.notifications.company')}
                      value={rule.company_id ?? ''}
                      onChange={(event) => edit(index, { company_id: event.target.value === '' ? null : event.target.value })}
                      className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
                    >
                      <option value="">{t('settings.notifications.allCompanies')}</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      aria-label={t('settings.notifications.type')}
                      list="notification-types"
                      value={rule.notification_type}
                      onChange={(event) => edit(index, { notification_type: event.target.value })}
                      className={cn(fieldStyles, 'w-full min-w-40 border-line px-2 py-1.5 font-mono text-sm')}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="checkbox"
                      aria-label={t('settings.notifications.inApp')}
                      className="size-4 accent-primary"
                      checked={rule.in_app}
                      onChange={(event) => edit(index, { in_app: event.target.checked })}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="checkbox"
                      aria-label={t('settings.notifications.email')}
                      className="size-4 accent-primary"
                      checked={rule.email}
                      onChange={(event) => edit(index, { email: event.target.checked })}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      aria-label={t('settings.notifications.minSeverity')}
                      value={rule.min_severity}
                      onChange={(event) => edit(index, { min_severity: event.target.value as NotificationSeverity })}
                      className={cn(fieldStyles, 'w-auto border-line px-2 py-1.5 text-sm')}
                    >
                      {SEVERITIES.map((value) => (
                        <option key={value} value={value}>
                          {t(`severity.${value}` as 'severity.info')}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      aria-label={t('settings.notifications.removeRule')}
                      onClick={() => setDraft(rows.filter((_, i) => i !== index))}
                      className="rounded-xs p-1.5 text-ink-faint transition-colors hover:bg-danger-pale hover:text-danger"
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <datalist id="notification-types">
        {COMMON_NOTIFICATION_TYPES.map((type) => (
          <option key={type} value={type} />
        ))}
      </datalist>

      <div className="flex items-center gap-2">
        <Button variant="secondary" icon={<Plus />} onClick={() => setDraft([...rows, { ...NEW_RULE }])}>
          {t('settings.notifications.addRule')}
        </Button>
        {draft !== null && (
          <Button busy={save.isPending} onClick={() => save.mutate()}>
            {t('settings.notifications.savePreferences')}
          </Button>
        )}
      </div>
    </Section>
  );
}
